import { createHttpService } from "../ApiService";
import {
  TRbacMatrix,
  TRole,
  TRoleWithPermissions,
  TCreateRoleRequest,
  TUpdateRolePermissionsRequest,
  TRoleUsersResponse,
} from "./rbac.interfaces";
import type { IRbac } from "./rbac.interfaces";
import {
  DEFAULT_LIST_PAGE,
  DEFAULT_LIST_PAGE_SIZE,
  IServerResponse,
} from "../common.interfaces";
import {
  rbacMatrixMock,
  rolePermissionsMap,
  initRolePermissionsForNewRole,
} from "../../__mocks__/rbac/rbacMock";
import { RBAC_PERMISSION_STRUCTURE } from "./rbac.constants";
import {
  clonePermissions,
  createEmptyPermissions,
  normalizeLegacyRbacPermissions,
} from "../../utils/rbacHelpers";
import {
  getRoleUsersMock,
  assignUserMock,
  unassignUserMock,
} from "../../__mocks__/rbac/rbacUsersMock";

export const useRbacService = () => {
  const httpAPI = createHttpService();
  const baseRbacPath = "";

  // Use mocks when VITE_APP_WITH_MOCK_DATA is set to "true"
  const appWithMockData = import.meta.env.VITE_APP_WITH_MOCK_DATA === "true";

  const getRbacMatrix = async (instituteId?: string): Promise<IServerResponse<TRbacMatrix>> => {
    if (appWithMockData) {
      const filteredData = instituteId
        ? {
            ...rbacMatrixMock,
            roles: rbacMatrixMock.roles.filter(
              (r) => "instituteId" in r && r.instituteId === instituteId,
            ),
          }
        : rbacMatrixMock;
      return Promise.resolve({
        success: true,
        message: null,
        data: filteredData,
        statusCode: 200,
        errorResponse: null,
      } as IServerResponse<TRbacMatrix>);
    }
    const roles = await httpAPI.rbac.get<TRole[]>(`${baseRbacPath}/roles`);
    return {
      success: true,
      message: null,
      data: {
        roles: roles ?? [],
        permissions: [],
        rolePermissions: [],
      },
      statusCode: 200,
      errorResponse: null,
    };
  };

  const getRoles = (instituteId: string) => {
    if (appWithMockData) {
      const roles = rbacMatrixMock.roles.filter(
        (r) => "instituteId" in r && r.instituteId === instituteId,
      );
      return Promise.resolve({
        success: true,
        message: null,
        data: roles,
        statusCode: 200,
        errorResponse: null,
      } as IServerResponse<TRole[]>);
    }
    return httpAPI.rbac.get<IServerResponse<TRole[]>>(
      `${baseRbacPath}/roles`,
    );
  };

  const getRoleById = async (
    instituteId: string,
    roleId: string
  ): Promise<IServerResponse<TRoleWithPermissions>> => {
    if (appWithMockData) {
      const role = rbacMatrixMock.roles.find(
        (r) => r.id === roleId && r.instituteId === instituteId
      );
      if (!role) {
        return Promise.resolve({
          success: false,
          message: "Role not found",
          data: null,
          statusCode: 404,
          errorResponse: null,
        } as IServerResponse<TRoleWithPermissions>);
      }
      const rawPerms =
        rolePermissionsMap[roleId] ??
        createEmptyPermissions(RBAC_PERMISSION_STRUCTURE);
      const permissions = normalizeLegacyRbacPermissions(rawPerms);
      return Promise.resolve({
        success: true,
        message: null,
        data: { ...role, permissions },
        statusCode: 200,
        errorResponse: null,
      } as IServerResponse<TRoleWithPermissions>);
    }
    const role = await httpAPI.rbac.get<TRoleWithPermissions>(
      `${baseRbacPath}/roles/${roleId}`
    );
    const rawPerms =
      role?.permissions ??
      createEmptyPermissions(RBAC_PERMISSION_STRUCTURE);
    const permissions = normalizeLegacyRbacPermissions(rawPerms);
    return {
      success: true,
      message: null,
      data: {
        ...role,
        permissions,
      },
      statusCode: 200,
      errorResponse: null,
    };
  };

  const createRole = (instituteId: string, payload: TCreateRoleRequest) => {
    if (appWithMockData) {
      const newRole: TRole = {
        id: `role-${Date.now()}`,
        name: payload.name,
        description: payload.description,
        instituteId,
      };
      rbacMatrixMock.roles.push(newRole);
      initRolePermissionsForNewRole(newRole.id);
      return Promise.resolve({
        success: true,
        message: null,
        data: newRole,
        statusCode: 200,
        errorResponse: null,
      } as IServerResponse<TRole>);
    }
    return httpAPI.rbac.post<IServerResponse<TRole>>(
      `${baseRbacPath}/roles`,
      payload,
    );
  };

  const updateRolePermissions = (request: TUpdateRolePermissionsRequest) => {
    if (appWithMockData) {
      return Promise.resolve({
        success: true,
        message: null,
        data: rbacMatrixMock,
        statusCode: 200,
        errorResponse: null,
      } as IServerResponse<TRbacMatrix>);
    }
    return httpAPI.rbac.put<IServerResponse<TRbacMatrix>>(
      `${baseRbacPath}/roles/${request.roleId}/permissions`,
      { permissionIds: request.permissionIds },
    );
  };

  const updateRolePermissionsRbac = (
    instituteId: string,
    roleId: string,
    permissions: IRbac
  ): Promise<IServerResponse<IRbac>> => {
    if (appWithMockData) {
      rolePermissionsMap[roleId] = permissions;
      return Promise.resolve({
        success: true,
        message: null,
        data: permissions,
        statusCode: 200,
        errorResponse: null,
      } as IServerResponse<IRbac>);
    }
    return httpAPI.rbac.put<IServerResponse<IRbac>>(
      `${baseRbacPath}/roles/${roleId}/permissions`,
      { permissions }
    );
  };

  const duplicateRole = async (
    instituteId: string,
    roleId: string
  ): Promise<IServerResponse<TRole>> => {
    const roleRes = await getRoleById(instituteId, roleId);
    if (!roleRes.success || !roleRes.data) {
      return {
        success: false,
        message: roleRes.message ?? "Role not found",
        data: null,
        statusCode: roleRes.statusCode ?? 404,
        errorResponse: null,
      } as IServerResponse<TRole>;
    }
    const role = roleRes.data;
    const createRes = await createRole(instituteId, {
      name: `${role.name} (Copy)`,
      description: role.description,
    });
    if (!createRes.success || !createRes.data) {
      return createRes as IServerResponse<TRole>;
    }
    const newRole = createRes.data;
    await updateRolePermissionsRbac(
      instituteId,
      newRole.id,
      clonePermissions(role.permissions)
    );
    return {
      success: true,
      message: null,
      data: newRole,
      statusCode: 200,
      errorResponse: null,
    } as IServerResponse<TRole>;
  };

  const getRoleUsers = async (
    instituteId: string,
    roleId: string,
    page: number = DEFAULT_LIST_PAGE,
    limit: number = DEFAULT_LIST_PAGE_SIZE,
  ): Promise<IServerResponse<TRoleUsersResponse>> => {
    if (appWithMockData) {
      const data = getRoleUsersMock(instituteId, roleId, page, limit);
      return Promise.resolve({
        success: true,
        message: null,
        data,
        statusCode: 200,
        errorResponse: null,
      } as IServerResponse<TRoleUsersResponse>);
    }
    const data = await httpAPI.rbac.get<TRoleUsersResponse>(
      `${baseRbacPath}/users`,
      { params: { roleId, page, limit } }
    );
    return {
      success: true,
      message: null,
      data: data ?? null,
      statusCode: 200,
      errorResponse: null,
    };
  };

  const assignUser = (
    instituteId: string,
    roleId: string,
    userId: string
  ) => {
    if (appWithMockData) {
      assignUserMock(instituteId, roleId, userId);
      return Promise.resolve({
        success: true,
        message: null,
        data: null,
        statusCode: 200,
        errorResponse: null,
      } as IServerResponse<null>);
    }
    return httpAPI.rbac.post<IServerResponse<null>>(
      `${baseRbacPath}/roles/${roleId}/users/${userId}`
    );
  };

  const unassignUser = (
    instituteId: string,
    roleId: string,
    userId: string
  ) => {
    if (appWithMockData) {
      unassignUserMock(instituteId, roleId, userId);
      return Promise.resolve({
        success: true,
        message: null,
        data: null,
        statusCode: 200,
        errorResponse: null,
      } as IServerResponse<null>);
    }
    return httpAPI.rbac.delete<IServerResponse<null>>(
      `${baseRbacPath}/roles/${roleId}/users/${userId}`
    );
  };

  const deleteRole = (instituteId: string, roleId: string) => {
    if (appWithMockData) {
      const idx = rbacMatrixMock.roles.findIndex(
        (r) => r.id === roleId && r.instituteId === instituteId
      );
      if (idx >= 0) {
        rbacMatrixMock.roles.splice(idx, 1);
        delete rolePermissionsMap[roleId];
      }
      return Promise.resolve({
        success: true,
        message: null,
        data: null,
        statusCode: 200,
        errorResponse: null,
      } as IServerResponse<null>);
    }
    return httpAPI.rbac.delete<IServerResponse<null>>(
      `${baseRbacPath}/roles/${roleId}`
    );
  };

  return {
    getRbacMatrix,
    getRoles,
    getRoleById,
    createRole,
    updateRolePermissions,
    updateRolePermissionsRbac,
    duplicateRole,
    getRoleUsers,
    assignUser,
    unassignUser,
    deleteRole,
  };
};
